/**
 * badgeConfig.js — Badge definitions with inline SVG art.
 *
 * Each badge is tied to a quest (via questId) or a standalone condition.
 * The SVG art uses theme color tokens passed at render time.
 *
 * Badge shape:
 *   id:       unique key
 *   title:    display name
 *   desc:     earned description
 *   questId:  quest that unlocks this badge (checked against s.questsCompleted)
 *   tier:     "bronze" | "silver" | "gold" | "diamond" — affects glow intensity
 *   art:      (colors) => SVG JSX — the badge visual
 */

/* ─── Reusable SVG primitives ─── */

const hex = (cx, cy, r, fill, stroke, sw = 1.5) => (
  <polygon
    points={Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(" ")}
    fill={fill} stroke={stroke} strokeWidth={sw}
  />
);

const shield = (cx, cy, s, fill, stroke, sw = 1.5) => (
  <path
    d={`M${cx} ${cy - s} L${cx + s * 0.85} ${cy - s * 0.4} L${cx + s * 0.85} ${cy + s * 0.2} Q${cx + s * 0.85} ${cy + s * 0.8} ${cx} ${cy + s} Q${cx - s * 0.85} ${cy + s * 0.8} ${cx - s * 0.85} ${cy + s * 0.2} L${cx - s * 0.85} ${cy - s * 0.4} Z`}
    fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round"
  />
);

const star = (cx, cy, r, fill, stroke, sw = 1.5) => {
  const pts = [];
  for (let i = 0; i < 5; i++) {
    const aOuter = (Math.PI * 2 * i) / 5 - Math.PI / 2;
    const aInner = aOuter + Math.PI / 5;
    pts.push(`${cx + r * Math.cos(aOuter)},${cy + r * Math.sin(aOuter)}`);
    pts.push(`${cx + r * 0.4 * Math.cos(aInner)},${cy + r * 0.4 * Math.sin(aInner)}`);
  }
  return <polygon points={pts.join(" ")} fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />;
};

const diamond = (cx, cy, s, fill, stroke, sw = 1.5) => (
  <polygon
    points={`${cx},${cy - s} ${cx + s * 0.7},${cy} ${cx},${cy + s} ${cx - s * 0.7},${cy}`}
    fill={fill} stroke={stroke} strokeWidth={sw} strokeLinejoin="round"
  />
);

/* ═══════════════════════════════════════════════════
   BADGE DEFINITIONS
   ═══════════════════════════════════════════════════ */

export const BADGES = [
  /* ─── SAFETY ─── */
  {
    id: "badge-safety-net",
    title: "Safety Net",
    desc: "Built your first $1K safety buffer",
    questId: "emergency-1k",
    tier: "bronze",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        {shield(32, 34, 22, `${cl.orange}18`, cl.orange, 1.8)}
        <text x="32" y="39" textAnchor="middle" fontSize="18" fill={cl.orange} fontWeight="700">$</text>
      </svg>
    ),
  },
  {
    id: "badge-fortress",
    title: "Fortress",
    desc: "Emergency fund fully funded",
    questId: "emergency-full",
    tier: "silver",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        {shield(32, 33, 24, `${cl.orange}20`, cl.orange, 2)}
        {shield(32, 33, 15, `${cl.orange}30`, cl.orange, 1)}
        <circle cx="32" cy="33" r="4" fill={cl.orange} />
      </svg>
    ),
  },

  /* ─── CREDIT ─── */
  {
    id: "badge-clean-slate",
    title: "Clean Slate",
    desc: "Zeroed out credit card debt",
    questId: "credit-zero",
    tier: "bronze",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <circle cx="32" cy="32" r="22" fill={`${cl.purple}15`} stroke={cl.purple} strokeWidth="1.8" />
        <path d="M24 32 L30 38 L42 26" fill="none" stroke={cl.purple} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "badge-excellent",
    title: "Excellent Credit",
    desc: "Reached 750+ credit score",
    questId: "credit-750",
    tier: "gold",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        {star(32, 32, 23, `${cl.purple}20`, cl.purple, 1.8)}
        <text x="32" y="37" textAnchor="middle" fontSize="12" fontWeight="800" fill={cl.purple}>750</text>
      </svg>
    ),
  },

  /* ─── BUDGET ─── */
  {
    id: "badge-budget-boss",
    title: "Budget Boss",
    desc: "Every category on target",
    questId: "budget-boss",
    tier: "bronze",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        {hex(32, 32, 22, `${cl.green}15`, cl.green, 1.8)}
        <text x="32" y="37" textAnchor="middle" fontSize="16" fill={cl.green}>🎯</text>
      </svg>
    ),
  },
  {
    id: "badge-connected",
    title: "Connected",
    desc: "Budget synced to projections",
    questId: "budget-sync-on",
    tier: "bronze",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <circle cx="24" cy="32" r="10" fill={`${cl.blue}15`} stroke={cl.blue} strokeWidth="1.5" />
        <circle cx="40" cy="32" r="10" fill={`${cl.green}15`} stroke={cl.green} strokeWidth="1.5" />
        <line x1="34" y1="32" x2="30" y2="32" stroke={cl.fg} strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },

  /* ─── INVEST ─── */
  {
    id: "badge-five-figures",
    title: "Five Figures",
    desc: "Net worth crossed $10K",
    questId: "nw-10k",
    tier: "bronze",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        {hex(32, 32, 23, `${cl.blue}12`, cl.blue, 1.8)}
        <text x="32" y="37" textAnchor="middle" fontSize="11" fontWeight="800" fill={cl.blue}>$10K</text>
      </svg>
    ),
  },
  {
    id: "badge-fifty-k",
    title: "Momentum",
    desc: "Net worth reached $50K",
    questId: "nw-50k",
    tier: "silver",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        {diamond(32, 32, 22, `${cl.blue}18`, cl.blue, 1.8)}
        <text x="32" y="37" textAnchor="middle" fontSize="10" fontWeight="800" fill={cl.blue}>$50K</text>
      </svg>
    ),
  },
  {
    id: "badge-hundred-k",
    title: "The Hardest $100K",
    desc: "Conquered the hardest milestone in wealth building",
    questId: "nw-100k",
    tier: "diamond",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <defs>
          <linearGradient id="bg100k" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={cl.blue} stopOpacity="0.2" />
            <stop offset="100%" stopColor={cl.purple} stopOpacity="0.2" />
          </linearGradient>
          <linearGradient id="st100k" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={cl.blue} />
            <stop offset="100%" stopColor={cl.purple} />
          </linearGradient>
        </defs>
        {diamond(32, 32, 24, "url(#bg100k)", "url(#st100k)", 2)}
        <text x="32" y="36" textAnchor="middle" fontSize="9" fontWeight="800" fill={cl.fg}>$100K</text>
      </svg>
    ),
  },

  /* ─── STREAKS ─── */
  {
    id: "badge-week-warrior",
    title: "Week Warrior",
    desc: "7-day check-in streak",
    questId: "streak-7",
    tier: "silver",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <circle cx="32" cy="32" r="22" fill={`${cl.red}12`} stroke={cl.red} strokeWidth="1.8" />
        <text x="32" y="30" textAnchor="middle" fontSize="18" fill={cl.red}>🔥</text>
        <text x="32" y="44" textAnchor="middle" fontSize="9" fontWeight="700" fill={cl.red}>7</text>
      </svg>
    ),
  },
  {
    id: "badge-thirty-legend",
    title: "30-Day Legend",
    desc: "Unbroken 30-day streak",
    questId: "streak-30",
    tier: "gold",
    art: (cl) => (
      <svg viewBox="0 0 64 64" width="100%" height="100%">
        <defs>
          <linearGradient id="bgLeg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={cl.orange} stopOpacity="0.2" />
            <stop offset="100%" stopColor={cl.red} stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {star(32, 32, 24, "url(#bgLeg)", cl.orange, 2)}
        <text x="32" y="30" textAnchor="middle" fontSize="14" fill={cl.orange}>⚡</text>
        <text x="32" y="43" textAnchor="middle" fontSize="8" fontWeight="700" fill={cl.orange}>30</text>
      </svg>
    ),
  },
];

/**
 * Tier glow intensities for earned badges.
 */
export const TIER_GLOW = {
  bronze:  { blur: 8,  spread: 2,  opacity: 0.15 },
  silver:  { blur: 12, spread: 3,  opacity: 0.20 },
  gold:    { blur: 16, spread: 4,  opacity: 0.30 },
  diamond: { blur: 20, spread: 5,  opacity: 0.40 },
};

/**
 * Tier accent color for glow effect.
 */
export const TIER_COLOR = {
  bronze:  "#CD7F32",
  silver:  "#C0C0C0",
  gold:    "#FFD700",
  diamond: "#B9F2FF",
};
